import { useState } from 'react'
import axios from 'axios'
import { Trans, useTranslation } from 'react-i18next'
import Papa from 'papaparse'

import { BulkRejectRowErrorsTable } from '../../../agentVerification/agentVerificationBulkRejectModal/BulkRejectRowErrorsTable'
import { downloadCsv } from '../../../agentVerification/agentVerificationBulkRejectModal/downloadCsv'

import styles from './BulkReleaseHoldsModal.module.scss'
import {
  BULK_RELEASE_ID_HEADER,
  BULK_RELEASE_MAX_CSV_SIZE_BYTES,
  BULK_RELEASE_MAX_ROWS,
  ParsedBulkReleaseCsv,
  parseBulkReleaseCsv
} from './parseBulkReleaseCsv'
import { BulkReleaseHoldsResult } from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import { getApiErrorCode } from '@percent/admin-dashboard/common/library/getApiErrorCode'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import {
  Alert,
  Badge,
  ButtonText,
  ConfirmationModal,
  Dropzone,
  Modal,
  Spacer,
  Table,
  TableBody,
  TableCell,
  TableProvider,
  TableRow,
  theme
} from '@percent/lemonade'
import { useMutation } from '@percent/utility'

const MODAL_WIDTH_STANDARD = 560
const MODAL_WIDTH_WIDE = 860
const NOT_RELEASED_CSV_FILENAME = 'bulk-release-not-released.csv'

// Spelled out rather than via Mantine's `MIME_TYPES`, which apps must not import directly.
const ACCEPTED_FILE_TYPES = ['text/csv']

type View = 'upload' | 'confirm' | 'submitting' | 'result' | 'error'

type RowError = { row: number; field: string; fieldValue?: string; message: string }

type BulkReleaseErrorMessageKey =
  | 'dialog.bulkReleaseHolds.error.invalidCsvFile'
  | 'dialog.bulkReleaseHolds.error.invalidRecords'
  | 'dialog.bulkReleaseHolds.error.unknown'

const UNKNOWN_ERROR_MESSAGE_KEY: BulkReleaseErrorMessageKey = 'dialog.bulkReleaseHolds.error.unknown'

const ERROR_MESSAGE_KEY_BY_CODE: Record<string, BulkReleaseErrorMessageKey> = {
  'validation_submission/bulk_release/invalid_csv_file': 'dialog.bulkReleaseHolds.error.invalidCsvFile',
  'validation_submission/bulk_release/invalid_records': 'dialog.bulkReleaseHolds.error.invalidRecords'
}

const isRowError = (value: unknown): value is RowError =>
  typeof value === 'object' &&
  value !== null &&
  'row' in value &&
  typeof value.row === 'number' &&
  'field' in value &&
  typeof value.field === 'string' &&
  'message' in value &&
  typeof value.message === 'string'

const getRowErrors = (err: unknown): RowError[] => {
  if (!axios.isAxiosError(err)) return []

  const rowErrors = err.response?.data?.error?.rowErrors

  return Array.isArray(rowErrors) ? rowErrors.filter(isRowError) : []
}

type NotReleased = { id: string; outcome: 'notHeld' | 'notFound' }

const toNotReleased = ({ notHeld, notFound }: BulkReleaseHoldsResult): NotReleased[] => [
  ...notHeld.map(id => ({ id, outcome: 'notHeld' as const })),
  ...notFound.map(id => ({ id, outcome: 'notFound' as const }))
]

type BulkReleaseHoldsModalProps = {
  onClose: VoidFunction
  onReleased: VoidFunction
}

export function BulkReleaseHoldsModal({ onClose, onReleased }: Readonly<BulkReleaseHoldsModalProps>) {
  const { t } = useTranslation()
  const { validationSubmissionsService } = useServices()
  const [view, setView] = useState<View>('upload')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [parsed, setParsed] = useState<ParsedBulkReleaseCsv | null>(null)
  const [result, setResult] = useState<BulkReleaseHoldsResult | null>(null)
  const [error, setError] = useState<unknown>(null)

  const [, { mutateAsync: bulkRelease }] = useMutation(
    validationSubmissionsService.bulkReleaseValidationSubmissionHolds
  )

  const close = () => {
    if (result && result.released.length > 0) onReleased()
    onClose()
  }

  const review = async () => {
    setParsed(parseBulkReleaseCsv(await selectedFiles[0].text()))
    setView('confirm')
  }

  const submit = async () => {
    if (!parsed) return
    setView('submitting')

    try {
      const response = await bulkRelease(
        Papa.unparse({ fields: [BULK_RELEASE_ID_HEADER], data: parsed.ids.map(id => [id]) })
      )
      setResult(response.data)
      setView('result')
    } catch (err) {
      setError(err)
      setView('error')
    }
  }

  const renderUploadView = () => (
    <ConfirmationModal
      title={t('dialog.bulkReleaseHolds.title')}
      primaryButtonText={t('button.continue')}
      primaryBtnTestId="bulk-release-review-button"
      secondaryButtonText={t('button.cancel')}
      handleClose={close}
      handleSubmit={review}
      disabled={selectedFiles.length !== 1}
    >
      <Alert variant="info">
        <strong>{t('dialog.bulkReleaseHolds.alert.title')}</strong>
        <div>{t('dialog.bulkReleaseHolds.alert.description', { maxRows: BULK_RELEASE_MAX_ROWS })}</div>
        <ul style={{ paddingLeft: theme.spacing.m }}>
          <li>
            <Trans
              i18nKey="dialog.bulkReleaseHolds.alert.columns.validationSubmissionId"
              components={{ b: <strong />, i: <i /> }}
            />
          </li>
        </ul>
        <div>{t('dialog.bulkReleaseHolds.alert.export')}</div>
      </Alert>
      <Spacer size={4} />
      <Dropzone
        title={t('dialog.bulkReleaseHolds.dropzoneTitle')}
        description={t('dialog.bulkReleaseHolds.dropzoneDescription')}
        files={selectedFiles}
        maxFiles={1}
        onChange={setSelectedFiles}
        accept={ACCEPTED_FILE_TYPES}
        maxSize={BULK_RELEASE_MAX_CSV_SIZE_BYTES}
        testId="bulk-release-dropzone"
      />
    </ConfirmationModal>
  )

  const renderConfirmBody = (count: number) => {
    if (!parsed?.hasIdHeader) {
      return (
        <Alert variant="error" title={t('dialog.bulkReleaseHolds.confirm.missingHeader.title')}>
          {t('dialog.bulkReleaseHolds.confirm.missingHeader.description', { header: BULK_RELEASE_ID_HEADER })}
        </Alert>
      )
    }

    if (count === 0) {
      return (
        <Alert variant="error" title={t('dialog.bulkReleaseHolds.confirm.empty.title')}>
          {t('dialog.bulkReleaseHolds.confirm.empty.description')}
        </Alert>
      )
    }

    if (count > BULK_RELEASE_MAX_ROWS) {
      return (
        <Alert variant="error" title={t('dialog.bulkReleaseHolds.confirm.tooManyRows.title')}>
          {t('dialog.bulkReleaseHolds.confirm.tooManyRows.description', { count, maxRows: BULK_RELEASE_MAX_ROWS })}
        </Alert>
      )
    }

    return (
      <Alert variant="warning" title={t('dialog.bulkReleaseHolds.confirm.warning.title')}>
        {t('dialog.bulkReleaseHolds.confirm.warning.description', { count })}
      </Alert>
    )
  }

  const renderConfirmView = () => {
    const count = parsed?.ids.length ?? 0
    const canSubmit = !!parsed?.hasIdHeader && count > 0 && count <= BULK_RELEASE_MAX_ROWS
    const isSubmitting = view === 'submitting'

    return (
      <ConfirmationModal
        title={t('dialog.bulkReleaseHolds.confirm.title')}
        primaryButtonText={t('dialog.bulkReleaseHolds.confirm.submit', { count })}
        primaryBtnTestId="bulk-release-submit-button"
        secondaryButtonText={t('button.cancel')}
        handleClose={close}
        handleSubmit={submit}
        loading={isSubmitting}
        disabled={!canSubmit || isSubmitting}
      >
        {renderConfirmBody(count)}
        {(parsed?.duplicateCount ?? 0) > 0 && (
          <>
            <Spacer size={2} />
            <Alert variant="info" title={t('dialog.bulkReleaseHolds.confirm.duplicates.title')}>
              {t('dialog.bulkReleaseHolds.confirm.duplicates.description', { count: parsed?.duplicateCount })}
            </Alert>
          </>
        )}
      </ConfirmationModal>
    )
  }

  const renderResultView = () => {
    const notReleased = result ? toNotReleased(result) : []

    const downloadNotReleased = () =>
      downloadCsv(
        Papa.unparse({
          fields: [BULK_RELEASE_ID_HEADER, 'outcome'],
          data: notReleased.map(({ id, outcome }) => [id, outcome])
        }),
        NOT_RELEASED_CSV_FILENAME
      )

    return (
      <ConfirmationModal
        title={t('dialog.bulkReleaseHolds.result.title')}
        primaryButtonText={t('button.close')}
        handleClose={close}
        handleSubmit={close}
      >
        <Alert
          variant={notReleased.length > 0 ? 'warning' : 'info'}
          title={t('dialog.bulkReleaseHolds.result.releasedCount', { count: result?.released.length ?? 0 })}
        >
          {notReleased.length > 0
            ? t('dialog.bulkReleaseHolds.result.notReleasedDescription', { count: notReleased.length })
            : t('dialog.bulkReleaseHolds.result.allReleased')}
        </Alert>
        {notReleased.length > 0 && (
          <>
            <Spacer size={4} />
            <div className={styles.resultTableContainer} data-testid="bulk-release-not-released-table">
              <TableProvider
                columns={[
                  { id: t('dialog.bulkReleaseHolds.result.column.validationSubmissionId') },
                  { id: t('dialog.bulkReleaseHolds.result.column.outcome') }
                ]}
                totalResults={notReleased.length}
                isLoading={false}
                illustration="no-results"
                emptyTableTitle={t('dialog.bulkReleaseHolds.result.allReleased')}
              >
                <Table>
                  <TableBody>
                    {notReleased.map(({ id, outcome }) => (
                      <TableRow key={id}>
                        <TableCell>{id}</TableCell>
                        <TableCell>
                          <Badge variant="warning">{t(`dialog.bulkReleaseHolds.result.outcome.${outcome}`)}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableProvider>
            </div>
            <Spacer size={2} />
            <ButtonText variant="primary" onClick={downloadNotReleased}>
              {t('dialog.bulkReleaseHolds.result.downloadNotReleased')}
            </ButtonText>
          </>
        )}
      </ConfirmationModal>
    )
  }

  const rowErrors = getRowErrors(error)

  const renderErrorView = () => {
    const errorCode = getApiErrorCode(error)

    return (
      <ConfirmationModal
        title={t('dialog.bulkReleaseHolds.error.title')}
        primaryButtonText={t('button.close')}
        handleClose={close}
        handleSubmit={close}
      >
        <Alert variant="error" title={t('dialog.bulkReleaseHolds.error.nothingReleased')}>
          {t((errorCode && ERROR_MESSAGE_KEY_BY_CODE[errorCode]) || UNKNOWN_ERROR_MESSAGE_KEY)}
        </Alert>
        {rowErrors.length > 0 && (
          <>
            <Spacer size={4} />
            <BulkRejectRowErrorsTable rowErrors={rowErrors} />
          </>
        )}
      </ConfirmationModal>
    )
  }

  const isWideView =
    (view === 'result' && !!result && toNotReleased(result).length > 0) || (view === 'error' && rowErrors.length > 0)

  return (
    <Modal open onClose={close} width={isWideView ? MODAL_WIDTH_WIDE : MODAL_WIDTH_STANDARD}>
      {view === 'upload' && renderUploadView()}
      {(view === 'confirm' || view === 'submitting') && renderConfirmView()}
      {view === 'result' && renderResultView()}
      {view === 'error' && renderErrorView()}
    </Modal>
  )
}
